import { logger } from 'module/logger';
import { Editor } from '@tiptap/core';
import { StreamAPI } from 'api/stream';
import {
  collectRlCustomSectionTemplateSectionIds,
  groupHtmlFormattingFilterRowsByPromptId,
  streamTokens,
} from 'helpers/stream/tokens-streaming';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { MeetingPageThinkingLogsProps } from 'components/pages/meeting-page-template/meeting-page-thinking-log-row';

export type PageGenerationPreviewStreamingLogContext = {
  thinkingStreamFailed: string;
  htmlFormattingTokensFailed: string;
  postProcessingFailed: string;
};

export interface UsePageGenerationPreviewStreamingOptions {
  /** Resets stream/thinking state when this or `channelName` changes */
  resetKey: number;
  streaming?: boolean;
  token?: string;
  channelName?: string;
  previewContent?: unknown;
  shouldFilterByPromptId?: boolean;
  onPostStream?: (content: unknown) => void;
  logContext: PageGenerationPreviewStreamingLogContext;
}

export const usePageGenerationPreviewStreaming = ({
  resetKey,
  streaming = true,
  token,
  channelName,
  previewContent,
  shouldFilterByPromptId = true,
  onPostStream,
  logContext,
}: UsePageGenerationPreviewStreamingOptions) => {
  const intl = useIntl();
  const [isInitializingStream, setIsInitializingStream] = useState(false);
  const [streamedContent, setStreamedContent] = useState<unknown>(null);
  const [thinkingLogDescriptions, setThinkingLogDescriptions] = useState<
    string[]
  >([]);
  const [thinkingStreamActive, setThinkingStreamActive] = useState(false);
  const [thinkingPanelShown, setThinkingPanelShown] = useState(false);
  const [thinkingDurationSeconds, setThinkingDurationSeconds] = useState<
    number | null
  >(null);
  const thinkingStartedAtRef = useRef<number | null>(null);
  const thinkingStreamAbortRef = useRef<AbortController | null>(null);

  const onStreamContentStart = useCallback(() => {
    setIsInitializingStream(true);
  }, []);

  useEffect(() => {
    setStreamedContent(null);
    setThinkingLogDescriptions([]);
    setThinkingStreamActive(false);
    setThinkingPanelShown(false);
    setThinkingDurationSeconds(null);
    thinkingStartedAtRef.current = null;
    thinkingStreamAbortRef.current?.abort();
    thinkingStreamAbortRef.current = null;
    setIsInitializingStream(false);
  }, [resetKey, channelName]);

  useEffect(() => {
    return () => {
      thinkingStreamAbortRef.current?.abort();
    };
  }, []);

  const templateSectionIds = useMemo(() => {
    const fromContent = collectRlCustomSectionTemplateSectionIds(
      previewContent as Parameters<
        typeof collectRlCustomSectionTemplateSectionIds
      >[0],
    );

    if (fromContent.length > 0) {
      return fromContent;
    }

    return ['-1'];
  }, [previewContent]);

  const onInitialization = async (editor: Editor) => {
    if (!streaming || !token || !channelName) {
      return;
    }

    thinkingStreamAbortRef.current?.abort();
    thinkingStreamAbortRef.current = new AbortController();
    const { signal } = thinkingStreamAbortRef.current;

    setThinkingLogDescriptions([]);
    setThinkingStreamActive(true);
    setThinkingPanelShown(true);
    setThinkingDurationSeconds(null);
    thinkingStartedAtRef.current = Date.now();

    try {
      await StreamAPI.streamPageGenerationThinkingLogs({
        token,
        channelName,
        signal,
        onThinkingStep: (event) => {
          const description =
            typeof event.data?.description === 'string'
              ? event.data.description.trim()
              : '';

          if (!description) {
            return;
          }

          setThinkingLogDescriptions((prev) => [...prev, description]);
        },
      });
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        return;
      }

      logger.error(logContext.thinkingStreamFailed, { error: e });
    } finally {
      setThinkingStreamActive(false);
      if (thinkingStartedAtRef.current != null) {
        const elapsedMs = Date.now() - thinkingStartedAtRef.current;

        setThinkingDurationSeconds(Math.max(1, Math.round(elapsedMs / 1000)));
        thinkingStartedAtRef.current = null;
      }
    }

    if (signal.aborted) {
      return;
    }

    let playbackTokensByPromptId: Record<string, string[]>;

    try {
      const rows = await StreamAPI.getRunningHtmlFormattingTokens({
        token,
        channelName,
      });

      playbackTokensByPromptId = groupHtmlFormattingFilterRowsByPromptId(rows);
    } catch (e) {
      logger.error(logContext.htmlFormattingTokensFailed, { error: e });
      playbackTokensByPromptId = {};
    }

    await streamTokens({
      editor,
      identifierType: 'templateSectionId',
      identifierValues: templateSectionIds,
      token,
      channelName,
      shouldFilterByPromptId,
      mockHtmlTokensByPromptId: playbackTokensByPromptId,
      onStart: onStreamContentStart,
      onPostStream: async () => {
        try {
          const res = await StreamAPI.getPostProcessingContent({
            token,
            channelName,
          });

          const content = res[0]?.data?.content;

          setStreamedContent(content);
          onPostStream?.(content);
        } catch (e) {
          logger.error(logContext.postProcessingFailed, { error: e });
        }
      },
    });
  };

  const completedSummaryLabel =
    !thinkingStreamActive && thinkingDurationSeconds != null
      ? intl.formatMessage(
          { id: 'preview.meetingPage.thoughtForSeconds' },
          { seconds: thinkingDurationSeconds },
        )
      : undefined;

  const thinkingLogs: MeetingPageThinkingLogsProps | undefined =
    thinkingPanelShown
      ? {
          descriptions: thinkingLogDescriptions,
          isStreaming: thinkingStreamActive,
          completedSummaryLabel,
        }
      : undefined;

  return {
    isInitializingStream,
    streamedContent,
    onInitialization,
    thinkingLogs,
  };
};
